import { create } from "zustand";
import toast from "react-hot-toast";
import { RepositoryManager } from "../../data/repositoryManager";

export interface Category {
    id: number;
    name: string;
    display_name: string;
    subtype_name: string | null;
    display_order: number;
}

export interface ActivityGroup {
    id: number;
    key: string;
    name: string;
    description: string;
    display_order: number;
    is_active: boolean;
    categories: Category[];
    created_at: string;
    updated_at: string;
}

interface PaginationLinks {
    url: string | null;
    label: string;
    page: number | null;
    active: boolean;
}

interface ActivityGroupsResponse {
    current_page: number;
    data: ActivityGroup[];
    first_page_url: string;
    from: number;
    last_page: number;
    last_page_url: string;
    links: PaginationLinks[];
    next_page_url: string | null;
    path: string;
    per_page: number;
    prev_page_url: string | null;
    to: number;
    total: number;
}

interface ActivityGroupsStoreState {
    groupsData: ActivityGroupsResponse | null;
    groupDetail: ActivityGroup | null;
    error: string | null;

    isListLoading: boolean;
    isDetailLoading: boolean;
    isSaving: boolean;
    isDeleting: boolean;

    getGroups: (params?: any) => Promise<{ success: boolean; data?: ActivityGroupsResponse; error?: string }>;
    getGroupById: (id: number | string) => Promise<{ success: boolean; data?: ActivityGroup; error?: string }>;
    createGroup: (payload: Partial<ActivityGroup>) => Promise<{ success: boolean; data?: ActivityGroup; error?: string }>;
    updateGroup: (id: number | string, payload: Partial<ActivityGroup>) => Promise<{ success: boolean; data?: ActivityGroup; error?: string }>;
    deleteGroup: (id: number | string) => Promise<{ success: boolean; error?: string }>;
    syncCategories: (id: number | string, categories: Category[]) => Promise<{ success: boolean; error?: string }>;
}

const useActivityGroupsStore = create<ActivityGroupsStoreState>((set, get) => ({
    groupsData: null,
    groupDetail: null,
    error: null,

    isListLoading: false,
    isDetailLoading: false,
    isSaving: false,
    isDeleting: false,

    getGroups: async (params = {}) => {
        set({ isListLoading: true, error: null });
        try {
            const result = await RepositoryManager.activityGroups.getGroups(params);
            if (result.success && result.data) {
                // Transform API response to match expected structure
                const transformedData = {
                    current_page: result.data.current_page,
                    data: result.data.data,
                    first_page_url: "",
                    from: (result.data.current_page - 1) * result.data.per_page + 1,
                    last_page: result.data.last_page,
                    last_page_url: "",
                    links: [],
                    next_page_url: null,
                    path: "",
                    per_page: result.data.per_page,
                    prev_page_url: null,
                    to: result.data.current_page * result.data.per_page,
                    total: result.data.total,
                };
                set({ groupsData: transformedData, isListLoading: false });
                return { success: true, data: transformedData };
            }
            throw new Error(result.message || "Failed to fetch activity groups");
        } catch (err) {
            const message = err instanceof Error ? err.message : "Unknown error";
            set({ isListLoading: false, error: message });
            toast.error(message);
            return { success: false, error: message };
        }
    },

    getGroupById: async (id) => {
        set({ isDetailLoading: true, error: null });
        try {
            const result = await RepositoryManager.activityGroups.getGroupById(id);
            if (result.success && result.data) {
                set({ groupDetail: result.data, isDetailLoading: false });
                return { success: true, data: result.data };
            }
            throw new Error(result.message || "Failed to fetch group details");
        } catch (err) {
            const message = err instanceof Error ? err.message : "Unknown error";
            set({ isDetailLoading: false, error: message });
            toast.error(message);
            return { success: false, error: message };
        }
    },

    createGroup: async (payload) => {
        set({ isSaving: true, error: null });
        try {
            const result = await RepositoryManager.activityGroups.createGroup(payload);
            if (result.success && result.data) {
                const newGroup = result.data;
                const { groupsData } = get();
                if (groupsData) {
                    set({
                        groupsData: {
                            ...groupsData,
                            data: [newGroup, ...groupsData.data],
                            total: groupsData.total + 1,
                        },
                    });
                }
                toast.success("Group created successfully");
                set({ isSaving: false });
                return { success: true, data: newGroup };
            }
            throw new Error(result.message || "Failed to create group");
        } catch (err) {
            const message = err instanceof Error ? err.message : "Unknown error";
            set({ isSaving: false, error: message });
            toast.error(message);
            return { success: false, error: message };
        }
    },

    updateGroup: async (id, payload) => {
        set({ isSaving: true, error: null });
        try {
            const result = await RepositoryManager.activityGroups.updateGroup(id, payload);
            if (result.success && result.data) {
                const updatedGroup = result.data;
                const { groupsData } = get();
                if (groupsData) {
                    set({
                        groupsData: {
                            ...groupsData,
                            data: groupsData.data.map((g) => (g.id === updatedGroup.id ? updatedGroup : g)),
                        },
                    });
                }
                toast.success("Group updated successfully");
                set({ isSaving: false });
                return { success: true, data: updatedGroup };
            }
            throw new Error(result.message || "Failed to update group");
        } catch (err) {
            const message = err instanceof Error ? err.message : "Unknown error";
            set({ isSaving: false, error: message });
            toast.error(message);
            return { success: false, error: message };
        }
    },

    deleteGroup: async (id) => {
        set({ isDeleting: true, error: null });
        try {
            const result = await RepositoryManager.activityGroups.deleteGroup(id);
            if (result.success) {
                const { groupsData } = get();
                if (groupsData) {
                    set({
                        groupsData: {
                            ...groupsData,
                            data: groupsData.data.filter((g) => g.id !== id),
                            total: groupsData.total - 1,
                        },
                    });
                }
                toast.success("Group deleted successfully");
                set({ isDeleting: false });
                return { success: true };
            }
            throw new Error(result.message || "Failed to delete group");
        } catch (err) {
            const message = err instanceof Error ? err.message : "Unknown error";
            set({ isDeleting: false, error: message });
            toast.error(message);
            return { success: false, error: message };
        }
    },

    syncCategories: async (id, categories) => {
        set({ isSaving: true, error: null });
        try {
            const result = await RepositoryManager.activityGroups.syncCategories(id, categories);
            if (result.success) {
                toast.success("Categories synced successfully");
                set({ isSaving: false });
                return { success: true };
            }
            throw new Error(result.message || "Failed to sync categories");
        } catch (err) {
            const message = err instanceof Error ? err.message : "Unknown error";
            set({ isSaving: false, error: message });
            toast.error(message);
            return { success: false, error: message };
        }
    },
}));

export default useActivityGroupsStore;
